// © 2025 Ananda Kumar. All rights reserved.
// Unauthorized copying of this file, via any medium, is strictly prohibited.

import { Component, DestroyRef, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CountdownComponent } from '../shared/countdown/countdown.component';
import { AuthService } from '../../services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { ToastService } from '../../services/toast.service';
import { NavigationConfig } from '../models/navigationConfig.model';
import { BackgroundService } from '../../services/background.service';
import { TenantService } from '../../services/tenant.service';
import { ConfigurationService } from '../../services/configuration.service';
@Component({
  selector: 'app-home',
  standalone: false,
  templateUrl: './home.component.html',
  styleUrl: './home.component.css',
})
export class HomeComponent {
  private destroyRef = inject(DestroyRef);
  @ViewChild(CountdownComponent) countdownComponent!: CountdownComponent;

  couples: string[] = [];
  userRoles: string[] = [];
  eventOverview: any;
  kithKinMenuItems: any[] = [];
  menuBarMenuItems: any[] = [];
  navigationConfig: NavigationConfig = new NavigationConfig();
  isCoupleDetailsLoaded = false;
  showRsvpModal = false;
  tenantType: string | null = null;

  menuItems$!: Observable<any[]>;
  navigationConfig$!: Observable<NavigationConfig>;
  constructor(
    public tenantService: TenantService,
    private toastService: ToastService,
    private authService: AuthService,
    private backgroundService: BackgroundService,
    private activatedRoute: ActivatedRoute,
    private configurationService: ConfigurationService,
    private router: Router) {
    this.menuItems$ = this.configurationService.getMenu();
    this.navigationConfig$ = this.configurationService.getNavigationConfig();
  }

  isLoggedIn$!: Observable<boolean>;
  isAdmin$!: Observable<boolean>;

  get isLoggedIn() {
    return this.authService.isLoggedIn;
  }

  ngOnInit() {
    this.navigationConfig$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (nav) => this.navigationConfig = nav,
      error: (error) => console.error('Error building nav config:', error)
    });

    setTimeout(() => {
      this.isLoggedIn$ = this.authService.isLoggedIn$;
      this.isAdmin$ = this.authService.isAdmin$;
      this.tenantType = this.tenantService.getTenantType();
    });

    this.userRoles = this.authService.getUserRoles();

    this.activatedRoute.queryParamMap.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(params => {
      const selectedTab = params.get('selectedTab');
      if (selectedTab === 'rsvp') {
        this.openRsvpModal();
      }
    });
  }

  ngAfterViewInit() {
    this.loadBackgroundServices();
  }

  get kithKinConfig() {
    return this.navigationConfig.kithKin;
  }

  get menuBarConfig() {
    return this.navigationConfig.menuBar;
  }

  get galleryBarConfig() {
    return this.navigationConfig.galleryBar;
  }

  get galleryCardConfig() {
    return this.navigationConfig.galleryCard;
  }

  get rideShareConfig() {
    return this.navigationConfig.carPoolCard;
  }

  get exclusiveConfig() {
    return this.navigationConfig.exclusiveCard;
  }

  get eventOrganisersConfig() {
    return this.navigationConfig.eventOrganisers;
  }

  loadBackgroundServices() {
    this.backgroundService.loadPeopleList();
  }

  showExclusiveCard(): boolean {
    return this.exclusiveConfig.show &&
      (this.exclusiveConfig.rolesRequired.length === 0 ||
        this.exclusiveConfig.rolesRequired.some(role => this.userRoles.includes(role) || this.userRoles.includes('admin')));
  }

  triggerConfetti() {
    this.countdownComponent.launchConfetti()
  }

  logout() {
    this.authService.logout().subscribe({
      next: () => {
        this.toastService.success('Logged out successfully');
        // Redirect to tenant home
        const tenantKey = this.tenantService.getTenantKey();
        this.router.navigate([`/${tenantKey}`]);
      },
      error: (error) => {
        console.error('Logout failed:', error);
        this.toastService.error('Logout failed. Please try again.');
        // Even if logout fails, redirect to tenant home
        const tenantKey = this.tenantService.getTenantKey();
        this.router.navigate([`/${tenantKey}`]);
      }
    });
  }

  login() {
    this.router.navigateByUrl(this.tenantService.getTenantPrefixedPath('login').join());
  }

  openRsvpModal() {
    document.body.classList.add('modal-open');
    this.showRsvpModal = true;
  }

  closeRsvpModal() {
    document.body.classList.remove('modal-open');
    this.showRsvpModal = false;
  }
}